import type { LocaleSchema } from '../schema'
import type { ru } from './ru'

// Форма — как у базового ru (LocaleSchema): tsc падает на пропущенном/лишнем ключе и на
// потерянной {{переменной}}. Плюральные формы en — `_one/_other`.
export const en = {
  common: {
    cancel: 'cancel',
    delete: 'delete',
    deleteLabel: 'Delete',
    close: 'close',
    closeLabel: 'Close',
    save: 'save',
    saveSubmit: '[↵] save',
    revoke: 'revoke',
    loading: 'loading...',
    loadingLabel: 'loading',
    copied: '✓ copied',
    copyUrl: 'copy URL',
    userName: 'username',
  },

  locale: {
    switchTo: 'Switch interface language to {{language}}',
  },

  nav: {
    downloads: 'downloads',
    library: 'library',
    subscriptions: 'subscriptions',
    feed: 'feed',
    tokens: 'tokens',
    cookies: 'cookies',
    keys: 'keys',
    mobile: 'Mobile navigation',
    admin: 'Administration',
    logout: 'Sign out',
  },

  auth: {
    login: {
      title: 'sign in with a passkey',
      description:
        'Press “sign in” and pick the passkey linked to this account. No passwords — just Face ID, Touch ID or a hardware key.',
      submit: '[↵] sign in',
      registerLink: 'no account? <b>register with an invite →</b>',
      recoveryLink: 'lost your passkey? <b>recover access</b>',
    },
    register: {
      title: 'register',
      description:
        'Enter a username and an invite token. The browser will then ask you to register a passkey — Face ID, Touch ID or a hardware key.',
      inviteToken: 'invite token',
      invitePlaceholder: 'one of the CLI invites',
      submit: '[↵] create account',
      loginLink: 'already registered? <b>sign in →</b>',
    },
    recovery: {
      title: 'recover access',
      description:
        'Enter your username and one of your recovery codes. The browser will then ask you to register a new passkey. Old passkeys are NOT removed automatically — revoke them under “Keys”.',
      code: 'recovery code',
      codePlaceholder: 'one of the 10 codes',
      submit: '[↵] recover and link a passkey',
      backLink: '← <b>back to sign in</b>',
    },
    codes: {
      title: 'save these codes',
      description:
        'Each code works only once. If you lose your passkey, there is no way back into the account without them. <b>these codes will never be shown again.</b>',
      copy: 'copy',
      download: 'download .txt',
      acknowledge: 'I have saved my recovery codes',
      submit: '[↵] continue',
    },
  },

  notFound: {
    title: 'page not found',
    description: 'The URL you followed does not exist or is no longer available.',
    home: '[↵] go home',
  },

  install: {
    title: 'Install podpipe',
    description: 'Add podpipe to your home screen — it opens like an app, without the address bar.',
    safariOnly: 'Installation is available in Safari.',
    install: 'Install',
    iosShare: 'Tap “Share” in the Safari toolbar',
    iosAddToHome: 'Choose “Add to Home Screen” and confirm',
    androidMenu: 'Open the browser menu (⋮)',
    androidInstall: 'Choose “Install app”',
  },

  combobox: {
    showList: 'Show list',
    empty: 'no matches',
  },

  dashboard: {
    wsConnected: 'system ready · ws connected',
    wsDisconnected: 'ws disconnected · polling every 5s',
    title: 'turn links<br/>into a <accent>personal</accent> podcast',
    description:
      '// paste a URL — yt-dlp downloads it, ffmpeg transcodes it, the RSS updates. your podcast client sees the new episode within a minute.',
    pasteLabel: 'Paste link from clipboard',
    pasteTitle: 'Paste link from clipboard (shift+↵ — probe right away)',
    pasteHint:
      '// <kbd>⇧</kbd>+<kbd>↵</kbd> — paste a link from the clipboard and probe it right away',
    downloadStarted: 'Download started',
    quotaExceeded: 'Quota exceeded — delete some episodes',
    clipboardNoLink: 'No link in the clipboard',
    clipboardDenied: 'No access to the clipboard',
    jobDeleted: 'Job deleted',
    noJobs: '// silence — no jobs',
    deleteTitle: 'Delete job?',
    deleteText:
      'The job card will be removed from the list. An episode that was already created (if any) stays in the library.',
    untitled: 'untitled',
    handlerNode: '// handler node',
    noNodes: '// no nodes available',
    hideFormats: '// hide format list',
    showFormats: '// pick formats manually ({{total}})',
    formatsSelected: '{{selected}} selected',
    startDownload: '[↵] start download',
    formatsHint:
      '// pick a video format + an audio format, or one combined format. If nothing is selected, yt-dlp picks one by preset.',
  },

  library: {
    episodeCount_one: '{{count}} episode',
    episodeCount_other: '{{count}} episodes',
    loading: 'loading episodes',
    empty: '// no episodes yet — go to the main page and add a URL',
    deleted: 'Episode deleted',
    deleteTitle: 'Delete episode?',
    deleteText:
      'The file is removed from S3 and the database. A podcast client that already downloaded the episode keeps playing it locally, but will see it gone on the next feed refresh. This cannot be undone.',
    listened: 'played',
  },

  subscriptions: {
    title: 'subscriptions',
    description:
      'Subscribe to a channel — the service checks it every ~15 minutes and queues new videos for download with the chosen preset. Finished episodes show up in the feed and in the job list on the main page.',
    subscribe: '+ subscribe',
    created: 'Subscription created',
    updated: 'Subscription updated',
    removed: 'Unsubscribed',
    loading: 'loading subscriptions',
    empty: '// no subscriptions — add a YouTube · VK · Rutube · Twitch channel',
    preset: 'preset: {{preset}}',
    active: 'active',
    paused: 'paused',
    checked: 'checked {{when}}',
    neverChecked: 'not checked yet',
    editPreset: 'preset',
    unsubscribe: 'unsubscribe',
    createTitle: 'new subscription',
    channelUrl: 'channel link',
    channelHint: 'YouTube, VK, Rutube and Twitch are supported.',
    downloadPreset: '// download preset',
    subscribeSubmit: '[↵] subscribe',
    editTitle: 'edit subscription',
    activeToggle: 'subscription active — check the channel and download new videos',
    unsubscribeTitle: 'unsubscribe?',
    unsubscribeText:
      'The channel will no longer be checked and new videos will not be downloaded. Episodes already created stay in the library.',
  },

  settings: {
    feed: {
      title: 'feed settings',
      description: 'How the feed looks in a podcast client — title, cover, category.',
      saved: 'Saved',
      ownerEmailHint:
        'Owner contact email (itunes:owner). Required to publish in the Apple Podcasts directory; optional for a private subscription by link.',
      languageHint: 'Type a locale code or pick one from the list. A custom code is allowed.',
      categoryHint: 'The category as Apple Podcasts will show it.',
    },
    tokens: {
      title: 'feed tokens',
      description:
        'Each token is a separate read-only URL for a podcast client. Revoking it immediately breaks the subscription that used the token.',
      create: '+ create token',
      revoked: 'Token revoked',
      loading: 'loading tokens',
      empty: '// no tokens — create the first one and connect a podcast client',
      createdAt: 'created {{when}}',
      lastUsed: 'last access: {{when}}',
      neverUsed: 'never used',
      createTitle: 'new feed token',
      name: 'name',
      nameHint: 'To remember which client uses this URL — handy when revoking.',
      createSubmit: '[↵] create',
      createdTitle: 'token created — save the URL',
      revokeTitle: 'revoke token?',
      revokeText:
        'Subscriptions to the feed URL with this token stop working immediately. This cannot be undone.',
      qrReady:
        '<accent>{{name}}</accent> is ready. <warn>The token is shown only now — it cannot be retrieved after closing this dialog.</warn>',
      addToApplePodcasts: 'Add to Apple Podcasts',
      appleHint: 'iPhone / Mac — point the camera at the QR code or tap the button',
      otherClientsHint:
        'Android · Overcast · Pocket Casts · Castro — paste this URL into the client',
      done: 'done',
    },
    cookies: {
      title: 'cookies',
      description:
        'For private/age-restricted content behind a login (YouTube etc.). Upload a Netscape cookies.txt for the domain — the service passes it to yt-dlp when the link matches the domain.',
      helpLabel: 'how to get cookies',
      add: '+ add',
      saved: 'cookies saved',
      removed: 'cookies deleted',
      loading: 'loading cookies',
      empty: '// no cookies uploaded — add a cookies.txt for a domain with a login',
      noExpiry: 'no expiry (session)',
      expired: 'expired {{when}}',
      expires: 'expire {{when}}',
      updated: 'updated {{when}}',
      uploadTitle: 'add cookies',
      file: 'cookies.txt file',
      domain: 'domain',
      domainHint: 'Second-level domain — applies to all subdomains (www, m, etc.).',
      helpPrompt: 'Not sure where to get the file? <help>browser instructions</help>',
      uploadSubmit: '[↵] upload',
      deleteTitle: 'delete cookies?',
      deleteText: 'Downloading login-only content from this domain will need fresh cookies again.',
      help: {
        title: 'how to get cookies.txt',
        intro:
          'You need a file in <b>Netscape cookies.txt</b> format for the domain you are signed in to. The first line of the file must be <code># Netscape HTTP Cookie File</code> — extensions write it automatically.',
        noExtension: 'no convenient extension',
        chromeInstall:
          'Install the extension from the Chrome Web Store (it exports strictly locally and sends nothing).',
        chromeLogin: 'Sign in to the site you need (e.g. youtube.com) in the same browser.',
        chromeOpen: 'Open a page of the site and click the extension icon.',
        chromeExport: 'Click Export → a cookies.txt file is saved.',
        firefoxInstall: 'Install the add-on from addons.mozilla.org.',
        firefoxLogin: 'Sign in to the site you need in Firefox.',
        firefoxOpen: 'Open a page of the site and click the add-on icon.',
        firefoxExport: 'Choose Current Site → Download / Export.',
        safariNone: 'There is no ready-made extension to export cookies.txt.',
        safariWorkaround:
          'The easiest way is to sign in with Chrome or Firefox and export from there.',
        safariFormat:
          'The resulting cookies.txt works just the same — the Netscape format is shared by all browsers.',
        outro:
          'Cookies expire: if downloads start failing with an authorization error, export and upload a fresh file (uploading the same domain again replaces the old one).',
      },
    },
    security: {
      title: 'passkeys & security',
      description:
        'Passkeys linked to the account. At least one must remain — otherwise you can only sign in with a recovery code.',
      add: '+ add passkey',
      added: 'Key added',
      removed: 'Key deleted',
      loading: 'loading keys',
      addedAt: 'added {{when}}',
      lastUsed: 'used {{when}}',
      neverUsed: 'never used',
      keepLast: 'Add a backup key first',
      addTitle: 'new passkey',
      keyName: 'key name',
      keyNameHint: 'So you can tell later which device is linked.',
      addSubmit: '[↵] confirm with key',
      deleteTitle: 'delete passkey?',
      deleteText:
        'After deletion you will not be able to sign in with this key. This cannot be undone.',
    },
  },

  admin: {
    users: {
      createInvite: '+ create invite',
      search: 'search by name…',
      loading: 'loading members',
      member: 'member',
      status: 'status',
      date: 'date',
      empty: 'no members',
      pagination: 'page {{page}} / {{pages}} · total {{total}}',
      prev: '← back',
      next: 'next →',
      inviteTitle: 'new invite',
      grantAdmin: 'grant administrator role',
      create: 'create',
      inviteCreated: 'invite created',
      tokenOnce: 'The token is shown only once — copy it now.',
      copyToken: 'copy token',
      copyLink: 'copy link',
      inviteUser: 'User: <b>{{name}}</b>',
      quotaTitle: 'edit quota',
      quotaUser: 'member: <b>{{name}}</b>',
      quotaUsage: '{{used}} used · current quota {{quota}}',
      newQuota: 'new quota',
      unitGiB: 'GiB',
      unitMiB: 'MiB',
      quotaZeroHint: '0 = the user cannot download anything.',
      quotaUpdated: 'Quota updated',
      quotaRequired: 'Enter a value',
      quotaNotNumber: 'Must be a number',
      quotaNegative: 'Cannot be negative',
      quotaTooLarge: 'Value is too large',
      invited: 'invited',
      expires: 'expires {{when}}',
      noQuota: 'no quota set',
      user: 'user',
      quota: 'quota',
      unban: 'unban',
      ban: 'ban',
      revokeAdmin: 'revoke admin',
      grantAdminShort: 'make admin',
    },
    library: {
      back: '← users',
      title: 'library: <accent>{{name}}</accent>',
      episodes: 'episodes',
      downloads: 'downloads',
      loadingEpisodes: 'loading episodes',
      loadingDownloads: 'loading downloads',
      noEpisodes: 'The user has no episodes',
      noDownloads: 'The user has no downloads',
      episodeTitle: 'title',
      date: 'date',
      size: 'size',
      job: 'job',
      status: 'status',
      progress: 'progress',
      createdAt: 'created',
      deleteTitle: 'Delete episode?',
      deleteText: 'The file is removed from S3 and the database. This cannot be undone.',
      jobStatus: {
        queued: 'queued',
        downloading: 'downloading',
        transcoding: 'transcoding',
        fetching: 'fetching',
        uploading: 'uploading',
        done: 'done',
        error: 'error',
      },
    },
    roles: {
      title: 'Manage <accent>roles</accent>',
      description: 'Grant and revoke the administrator role.',
      notice:
        'A role change takes effect only after a new access token is issued — on the next session refresh (automatic) or on the next sign-in.',
      userId: 'User ID',
      userIdHint: 'The user’s UUID from the database.',
      grant: 'make administrator',
      revoke: 'revoke role',
      registered: 'registered {{when}}',
      userNotFound: 'User not found',
    },
  },

  player: {
    label: 'Player',
    collapse: 'Collapse',
    expand: 'Expand player',
    close: 'Close player',
    rate: 'Speed {{rate}}',
    rateTitle: 'Playback speed',
    autoplay: 'Autoplay next episode',
    autoplayShort: 'autoplay',
    source: 'source →',
    urlCopied: 'URL copied',
    urlCopyFailed: 'Could not copy the URL',
    h265Note:
      '// H.265 is fully supported in Safari and partially in Chromium. If the player shows an error, open the episode in a podcast client.',
    episodeDeleted: 'Episode deleted',
    videoFailed: 'Could not play the video — H.265 is not supported in every browser',
    episodeFailed: 'Could not play the episode',
    continuedElsewhere: 'Playback continued on another device',
  },

  errors: {
    generic: 'Something went wrong',
    httpStatus: 'Error {{status}}',
    noRefreshToken: 'No refresh token',
    sessionExpired: 'Session expired',
    NOT_FOUND: 'Resource not found',
    FORBIDDEN: 'Insufficient permissions',
    INTERNAL: 'Internal server error',
    VALIDATION: 'Invalid request data',
    LAST_ADMIN: 'Cannot remove the role from the last administrator',
    USER_BANNED: 'Your account is banned',
    REQUIRED_TOKEN: 'Authorization required',
    INVALID_TOKEN: 'Session expired — sign in again',
    WRONG_TOKEN_TYPE: 'Wrong token type',
    TOKEN_REUSE: 'Session revoked — sign in again',
    INVALID_CREDENTIAL: 'This passkey is not linked to an account',
    INVALID_INVITE: 'The invite token is invalid or expired',
    INVALID_RECOVERY_CODE: 'The recovery code is invalid',
    WEBAUTHN_VERIFICATION: 'Could not verify the key — try again',
    INVALID_FEED_TOKEN: 'The feed token is invalid or revoked',
    FEED_TOKEN_NOT_FOUND: 'Feed token not found',
    EPISODE_NOT_FOUND: 'Episode not found',
    JOB_NOT_FOUND: 'Job not found',
    JOB_NOT_DELETABLE: 'The job cannot be deleted — it has not finished yet',
    PROBE_FAILED: 'yt-dlp could not read the resource — check the URL',
    DOWNLOAD_FAILED: 'Download failed — try again later',
    UNSUPPORTED_SOURCE: 'Source not supported',
    INVALID_DOWNLOAD_REQUEST: 'Invalid download parameters',
    SUBSCRIPTION_NOT_FOUND: 'Subscription not found',
    SUBSCRIPTION_ALREADY_EXISTS: 'You are already subscribed to this channel',
    NODES_UNAVAILABLE: 'No handler nodes available — try again later',
    COOKIE_NOT_FOUND: 'Cookies not found',
    INVALID_COOKIE_FILE: 'The file does not look like a Netscape cookies.txt',
    COOKIE_FILE_TOO_LARGE: 'The cookies file is too large',
    COOKIE_LIMIT_EXCEEDED: 'Saved cookies limit reached — delete the ones you do not need',
    QUOTA_EXCEEDED: 'Quota exceeded — delete episodes or raise the limit',
  },
} satisfies LocaleSchema<typeof ru>
